'use client';

import React, { useState, useMemo } from 'react';
import Link from 'next/link';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import SiteInternalLinks from '@/components/SiteInternalLinks';
import { allLocaties } from '@/data/locatiesData';
import PageHero from '@/components/responsive/PageHero';

const provinceColors: Record<string, string> = {
  'Oost-Vlaanderen': 'bg-blue-50 border-blue-200 text-blue-800',
  'Antwerpen': 'bg-red-50 border-red-200 text-red-800',
  'Brussels': 'bg-purple-50 border-purple-200 text-purple-800',
};

const provinceBadge: Record<string, string> = {
  'Oost-Vlaanderen': 'bg-blue-100 text-blue-700',
  'Antwerpen': 'bg-red-100 text-red-700',
  'Brussels': 'bg-purple-100 text-purple-700',
};

export default function LocatiesPage() {
  const [search, setSearch] = useState('');
  const [activeProvince, setActiveProvince] = useState<string>('Alle');

  const provinces = ['Alle', 'Oost-Vlaanderen', 'Antwerpen', 'Brussels'];

  const filtered = useMemo(() => {
    return allLocaties.filter((l) => {
      const matchSearch = l.city.toLowerCase().includes(search.toLowerCase());
      const matchProvince = activeProvince === 'Alle' || l.province === activeProvince;
      return matchSearch && matchProvince;
    });
  }, [search, activeProvince]);

  const groupedFiltered = useMemo(() => {
    const groups: Record<string, typeof filtered> = {};
    filtered.forEach((l) => {
      if (!groups[l.province]) groups[l.province] = [];
      groups[l.province].push(l);
    });
    return groups;
  }, [filtered]);

  return (
    <>
      <Header />
      <main>
        <PageHero
          title="Roetfilter Reinigen in Uw Regio"
          description="Roetfilter Center in Lokeren bedient klanten uit heel Oost-Vlaanderen, Antwerpen en Brussel. Zoek uw gemeente en ontdek hoe snel u bij ons bent."
          badge="Locaties"
          breadcrumbs={[
            { label: 'Home', href: '/' },
            { label: 'Locaties' },
          ]}
          chips={[
            'Oost-Vlaanderen',
            'Antwerpen',
            'Brussel',
            'Roetfilter reinigen',
            'Diagnose',
            '1 jaar garantie',
          ]}
        />

        {/* Province count pills */}
        <section className="bg-white border-b border-border py-4 lg:py-6">
          <div className="rc-container">
            <div className="flex flex-wrap justify-center gap-3 lg:gap-4 text-sm font-semibold">
              <span className="bg-secondary px-4 py-2 rounded-full text-foreground">
                {allLocaties.filter(l => l.province === 'Oost-Vlaanderen').length} gemeenten in Oost-Vlaanderen
              </span>
              <span className="bg-secondary px-4 py-2 rounded-full text-foreground">
                {allLocaties.filter(l => l.province === 'Antwerpen').length} gemeenten in Antwerpen
              </span>
              <span className="bg-secondary px-4 py-2 rounded-full text-foreground">
                {allLocaties.filter(l => l.province === 'Brussels').length} gemeenten in Brussel
              </span>
            </div>
          </div>
        </section>

        {/* Search + Filter */}
        <section className="py-6 lg:py-8 bg-white border-b border-border sticky top-16 z-30 shadow-sm">
          <div className="max-w-7xl mx-auto px-4 lg:px-8">
            <div className="flex flex-col sm:flex-row gap-4 items-center">
              <div className="relative flex-1 max-w-md w-full">
                <input
                  type="text"
                  placeholder="Zoek uw gemeente..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-border rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
                />
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">🔍</span>
              </div>
              <div className="flex gap-2 flex-wrap">
                {provinces.map((p) => (
                  <button
                    key={p}
                    onClick={() => setActiveProvince(p)}
                    className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors ${
                      activeProvince === p
                        ? 'bg-primary text-white' : 'bg-secondary text-muted-foreground hover:text-primary'
                    }`}
                  >
                    {p}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Priority cities */}
        {activeProvince === 'Alle' && !search && (
          <section className="py-8 lg:py-12 bg-secondary/20">
            <div className="rc-container">
              <h2 className="text-xl lg:text-2xl font-extrabold text-foreground mb-4 lg:mb-6">
                Prioriteitssteden — Snelste Service
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
                {allLocaties.filter(l => l.priority === 1).map((l) => (
                  <Link
                    key={l.slug}
                    href={`/locaties/${l.slug}`}
                    className="bg-white border-2 border-primary/20 rounded-xl p-3 text-center hover:border-primary hover:shadow-md transition-all group"
                  >
                    <p className="font-extrabold text-sm text-foreground group-hover:text-primary transition-colors">{l.city}</p>
                    <p className="text-xs text-muted-foreground mt-1">{l.driveTime}</p>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Existing dedicated pages */}
        {activeProvince === 'Alle' && !search && (
          <section className="py-6 lg:py-8 bg-white border-b border-border">
            <div className="rc-container">
              <h2 className="text-lg lg:text-xl font-extrabold text-foreground mb-4">Bestaande Stadspagina&apos;s</h2>
              <div className="flex flex-wrap gap-3">
                {[
                  { label: 'Gent', href: '/roetfilter-reinigen-gent' },
                  { label: 'Sint-Niklaas', href: '/roetfilter-reinigen-sint-niklaas' },
                  { label: 'Dendermonde', href: '/roetfilter-reinigen-dendermonde' },
                  { label: 'Lokeren', href: '/roetfilter-reinigen-lokeren' },
                  { label: 'Aalst', href: '/roetfilter-reinigen-aalst' },
                  { label: 'Temse', href: '/roetfilter-reinigen-temse' },
                  { label: 'Deinze', href: '/roetfilter-reinigen-deinze' },
                  { label: 'Evergem', href: '/roetfilter-reinigen-evergem' },
                ].map((p) => (
                  <Link
                    key={p.href}
                    href={p.href}
                    className="bg-primary/10 border border-primary/30 text-primary px-4 py-2 rounded-full text-sm font-semibold hover:bg-primary hover:text-white transition-colors"
                  >
                    {p.label}
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Province groups */}
        <section className="py-8 lg:py-12 bg-white">
          <div className="rc-container">
            {filtered.length === 0 ? (
              <div className="text-center py-12 lg:py-16">
                <p className="text-muted-foreground text-lg">Geen gemeente gevonden voor &quot;{search}&quot;</p>
                <button onClick={() => setSearch('')} className="mt-4 text-primary font-semibold hover:underline">
                  Zoekopdracht wissen
                </button>
              </div>
            ) : (
              Object.entries(groupedFiltered).map(([province, cities]) => (
                <div key={province} className="mb-8 lg:mb-12">
                  <div className="flex items-center gap-3 mb-4 lg:mb-6">
                    <h2 className="text-xl lg:text-2xl font-extrabold text-foreground">{province}</h2>
                    <span className={`text-xs font-bold px-3 py-1 rounded-full ${provinceBadge[province] || 'bg-gray-100 text-gray-700'}`}>
                      {cities.length} gemeenten
                    </span>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3">
                    {cities
                      .sort((a, b) => a.priority - b.priority || a.city.localeCompare(b.city))
                      .map((l) => (
                        <Link
                          key={l.slug}
                          href={`/locaties/${l.slug}`}
                          className={`rounded-xl p-3 border text-center hover:shadow-md transition-all group ${
                            l.priority === 1
                              ? 'bg-primary/5 border-primary/30 hover:border-primary' : 'bg-secondary/30 border-border hover:border-primary/50'
                          }`}
                        >
                          <p className={`font-semibold text-sm group-hover:text-primary transition-colors ${l.priority === 1 ? 'font-extrabold' : ''}`}>
                            {l.city}
                          </p>
                          <p className="text-xs text-muted-foreground mt-1">{l.driveTime}</p>
                        </Link>
                      ))}
                  </div>
                </div>
              ))
            )}
          </div>
        </section>

        {/* CTA */}
        <section className="py-12 lg:py-16 bg-primary text-white">
          <div className="rc-container max-w-4xl text-center">
            <h2 className="text-2xl lg:text-3xl font-extrabold mb-4">
              Uw gemeente staat er niet bij?
            </h2>
            <p className="text-white/80 mb-6 lg:mb-8">
              Bel ons op 0498 54 54 74. We helpen klanten uit heel Vlaanderen en Brussel.
              Dijkstraat 22, 9160 Lokeren.
            </p>
            <div className="flex flex-col sm:flex-row flex-wrap justify-center gap-3 lg:gap-4">
              <a href="tel:0498545474" className="bg-accent text-foreground px-8 py-4 rounded-full font-extrabold hover:opacity-90 transition-opacity">
                📞 BEL NU: 0498 54 54 74
              </a>
              <Link href="/offerte" className="bg-white/20 border-2 border-white/40 text-white px-8 py-4 rounded-full font-bold hover:bg-white/30 transition-colors">
                Gratis Offerte
              </Link>
            </div>
          </div>
        </section>

        <SiteInternalLinks variant="full" />
      </main>
      <Footer />
    </>
  );
}
